// Utility functions for aggregating worked time in attendance
import {
  TimesheetAggregatedWorkedTimeFragment,
  TimesheetWorkedTimeBlockFragment,
  TimesheetWorkedTimeFragment,
} from 'generated/resources/graphql'

export interface AggregatedWorkedTimeBlocks {
  breaksMinutesRounded: number
  minutes: number
  multipliedMinutes: number
  paidAbsencesMinutes: number
  paidBreaksMinutes: number
  pendingMinutes: number
  toleranceMinutesRounded: number
  trackedMinutes: number
  trackedMinutesDelta: number
  regularMinutes: number
  effectiveWorkedMinutes: number
  workedTimeBlocks: TimesheetWorkedTimeBlockFragment[]
  extraTimeMinutes: number
  approvedExtraTimeMinutes: number
}

class AttendanceWorkedTimeUtils {
  static aggregatedWorkedTimeBlocks(
    workedTimes: TimesheetWorkedTimeFragment[],
    aggregatedWorkedTime?: TimesheetAggregatedWorkedTimeFragment | null
  ): AggregatedWorkedTimeBlocks {
    const workedTimeBlocks = workedTimes.flatMap((wt) => wt.workedTimeBlocks || [])

    // Use paidBreaksMinutes from backend workedTimes, fallback to aggregatedWorkedTime only if workedTimes is empty
    const paidBreaksMinutes =
      workedTimes.length > 0
        ? workedTimes.reduce((sum, wt) => sum + (wt.paidBreaksMinutes || 0), 0)
        : aggregatedWorkedTime?.paidBreaksMinutes ?? 0

    return {
      breaksMinutesRounded: workedTimes.reduce(
        (sum, wt) => sum + (wt.breaksMinutesRounded || 0),
        0
      ),
      minutes: workedTimes.reduce((sum, wt) => sum + (wt.minutes || 0), 0),
      multipliedMinutes: workedTimes.reduce((sum, wt) => sum + (wt.multipliedMinutes || 0), 0),
      paidAbsencesMinutes: workedTimes.reduce((sum, wt) => sum + (wt.paidAbsencesMinutes || 0), 0),
      paidBreaksMinutes,
      pendingMinutes: workedTimes.reduce((sum, wt) => sum + (wt.pendingMinutes || 0), 0),
      toleranceMinutesRounded: workedTimes.reduce(
        (sum, wt) => sum + (wt.toleranceMinutesRounded || 0),
        0
      ),
      trackedMinutes: workedTimes.reduce((sum, wt) => sum + (wt.trackedMinutes || 0), 0),
      trackedMinutesDelta: workedTimes.reduce((sum, wt) => sum + (wt.trackedMinutesDelta || 0), 0),
      workedTimeBlocks,
      regularMinutes: workedTimes.reduce((sum, wt) => sum + (wt.regularMinutes || 0), 0),
      effectiveWorkedMinutes: workedTimes.reduce(
        (sum, wt) => sum + (wt.effectiveWorkedMinutes || 0),
        0
      ),
      extraTimeMinutes: workedTimes.reduce((sum, wt) => sum + (wt.extraTimeMinutes || 0), 0),
      approvedExtraTimeMinutes: workedTimes.reduce(
        (sum, wt) => sum + (wt.approvedExtraTimeMinutes || 0),
        0
      ),
    }
  }
}

export default AttendanceWorkedTimeUtils
